import React, { useEffect, useState, useCallback } from "react";
import { client, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

const ContactMessages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/contact-messages");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id, read) => {
    await client.patch(`/admin/contact-messages/${id}`, { read });
    setItems((s) => s.map((x) => (x.id === id ? { ...x, read } : x)));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await client.delete(`/admin/contact-messages/${id}`);
    setItems((s) => s.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-2">
        <Mail className="h-5 w-5 text-brand-orange" />
        <h1 className="font-display text-2xl font-bold text-navy">Contact Messages</h1>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div> : (
        <div className="space-y-4" data-testid="messages-list">
          {items.length === 0 && <p className="rounded-sm border border-dashed border-border bg-white py-16 text-center text-muted-foreground">No messages.</p>}
          {items.map((m) => (
            <div key={m.id} className={`rounded-sm border bg-white p-5 ${m.read ? "border-border" : "border-brand-orange"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-display font-semibold text-navy">{m.name}</span>
                    <span className="text-xs text-muted-foreground">{m.email}</span>
                    {!m.read && <span className="rounded-sm bg-brand-orange px-2 py-0.5 text-xs text-white">New</span>}
                  </div>
                  {m.subject && <p className="mt-1 text-sm font-medium text-navy">{m.subject}</p>}
                  <p className="mt-2 text-sm text-muted-foreground">{m.message}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => markRead(m.id, !m.read)} className="rounded-sm">
                    {m.read ? "Mark Unread" : "Mark Read"}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactMessages;
